<template>
  <b-field grouped class="columns">
    <b-field v-if="showBillingAddress" label="Billing Address" class="column">
      <div class="columns is-multiline">
        <div class="column is-full">
          <b-switch v-model="useCompanyAddress">
            Use company address
          </b-switch>
        </div>
        <div class="column is-full">
          <b-field label="Name on card">
            <b-input v-model="billingInfo.name"
                     :disabled="useCompanyAddress" />
          </b-field>
          <address-lookup v-model="billingInfo.address"
                          :disabled="useCompanyAddress"
          ></address-lookup>
        </div>
      </div>

    </b-field>
    <b-field label="Payment Information" class="column">
      <div class="columns is-multiline">
        <div class="column is-full">
          <b-switch v-model="billingInfo.doMonthlyInvoice">
            Send monthly invoice instead
            <small>(net 30)</small>
          </b-switch>
        </div>

        <div class="column is-full">
          <div ref="card-element" class="card-el-wrapper control ">
            <!-- A Stripe Element will be inserted here. -->
          </div>
          <b-notification :closable="false" type="is-light">
            <div class="content is-size-7" v-show="!billingInfo.doMonthlyInvoice">
              You may  see a temporary authorization for $1 from Truckl on your credit card statement.
              That is just us validating your card information.
            </div>
            <div class="content is-size-7" v-show="billingInfo.doMonthlyInvoice">
              Monthly invoice process fees apply
            </div>
          </b-notification>

          <div v-if="showBillingAddress" class="legal-agreements-wrapper">
            <div>
              <b-checkbox v-model="billingInfo.acceptAgreements"
                          :disabled="isPrePopulated"
                          v-validate="{required: true}"
                          name="terms and conditions">
                By clicking on this box I agree to the Truckl SaaS Terms and Conditions and the Truckl Privacy Policy.
              </b-checkbox>
            </div>

            <div class="columns has-text-centered">
              <div class="column">
                <a href="SaaS Agreement" @click.prevent="showSaasAgreement">Truckl SaaS Agreement</a>
              </div>

              <div class="column">
                <a href="Privacy Policy" @click.prevent="showPrivacyPolicy">Truckl Privacy Policy</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-field>

    <pdf-viewer :show.sync="showPdfViewer" :file-path="pdfViewerFile"></pdf-viewer>
  </b-field>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import Stripe from '@/api-interface/stripe'
import AddressLookup from '@/components/AddressLookup'
import PdfViewer from '@/components/ModalDisplayPdf'

const StripeElements = Stripe.elements()
export default {
  name: 'FormPaymentInformation',

  mounted() {
    this._copyCurrentCompany()
    this.createStripeInput()
    if (this.isUsingInvoice) {
      this.billingInfo.doMonthlyInvoice = true
    }
  },

  beforeDestroy() {
    this.removeStripeInput()
  },

  props: {
    showBillingAddress: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      stripCardEl: {},
      useCompanyAddress: true,
      pdfViewerFile: '',
      showPdfViewer: false,
      billingInfo: {
        acceptAgreements: false,
        name: '',
        address: {},
        stripeSrcToken: null,
        doMonthlyInvoice: false
      }
    }
  },
  computed: {
    ...mapGetters('user', { userInfo: 'accountInfo' }),
    ...mapState('company', ['currentCompanyInfo']),

    stripeAddressFormat() {
      return {
        address_line1: this.billingInfo.address.street1,
        address_line2: this.billingInfo.address.street2,
        address_city: this.billingInfo.address.city,
        address_state: this.billingInfo.address.state,
        address_zip: this.billingInfo.address.zip,
        address_country: 'US'
      }
    },

    isUsingInvoice() {
      return this.currentCompanyInfo.companyBillingMethod === 'manual'
    },

    isUsingCc() {
      return this.currentCompanyInfo.companyBillingMethod === 'automatic'
    }
  },

  methods: {
    showSaasAgreement() {
      this.pdfViewerFile = '/documents/Truckl LLC - SaaS Agreement.pdf'
      this.showPdfViewer = true
    },

    showPrivacyPolicy() {
      this.pdfViewerFile = '/documents/privacy-policy.pdf'
      this.showPdfViewer = true
    },

    _copyCurrentCompany() {
      this.billingInfo.name = this.currentCompanyInfo.companyName
      this.billingInfo.address = {
        street1: this.currentCompanyInfo.companyAddresses[0].companyAddressStreet1,
        zip: this.currentCompanyInfo.companyAddresses[0].companyAddressZip,
        state: this.currentCompanyInfo.companyAddresses[0].companyAddressState,
        city: this.currentCompanyInfo.companyAddresses[0].companyAddressCity
      }
    },

    async submitCardEl(elChangeEvent) {
      if (elChangeEvent.error || elChangeEvent.empty) {
        this.billingInfo.stripeSrcToken = null
      }

      if (elChangeEvent.complete) {
        try {
          const tokenData = {
            ...this.stripeAddressFormat,
            name: this.billingInfo.name
          }

          // ref: https://stripe.com/docs/stripe-js/reference#stripe-create-token
          const result = await Stripe.createToken(this.stripCardEl, tokenData)
          console.log(result)
          this.billingInfo.stripeSrcToken = result.token.id
        } catch (err) {
          // TODO: show error
          console.log(err)
          console.error('Something went wrong while trying to validate credit card')
        }
      }
    },

    _notify() {
      this.$emit('input', this.billingInfo)
    },

    createStripeInput() {
      const style = {
        base: {
          color: '#4a4a4a',
          fontFamily:
            'BlinkMacSystemFont, -apple-system, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4'
          }
        },
        invalid: {
          color: '#ff3860',
          iconColor: '#ff3860'
        }
      }

      const value = {
        postalCode: this.billingInfo.address.zip
      }

      this.stripCardEl = StripeElements.create('card', {
        iconStyle: 'solid',
        style,
        value,
        disabled: this.doMonthlyInvoice
      })

      this.stripCardEl.mount(this.$refs['card-element'])
      this.stripCardEl.addEventListener('change', this.submitCardEl)
    },

    removeStripeInput() {
      this.stripCardEl.destroy()
    }
  },

  components: {
    AddressLookup,
    PdfViewer
  },

  watch: {
    useCompanyAddress(newValue) {
      if (newValue) {
        this._copyCurrentCompany()
      }
    },

    acceptAgreements: '_notify',

    'billingInfo.doMonthlyInvoice'(newValue) {
      this.stripCardEl.update({ disabled: newValue })
      if (newValue) {
        this.stripCardEl.clear()
        this.billingInfo.stripeSrcToken = null
      }
    },

    billingInfo: {
      deep: true,
      handler: '_notify'
    }
  }
}
</script>

<style scoped lang="scss">
.card-el-wrapper {
  max-width: 100%;
  width: 100%;
  position: relative;
  background-color: white;
  padding: 7px 9px;
  margin-bottom: 12px;
  box-shadow: inset 0 1px 2px rgba(10, 10, 10, 0.1);
  border-radius: 4px;
  border: 1px solid #dbdbdb;
}

.legal-agreements-wrapper {
  background: white;
  border-radius: 5px;
  padding: 8px;

  .columns {
    margin-top: 8px;
  }
}
</style>
